import { Camera, Loader2, Upload, X } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import type { AvatarUploadResult } from '../../utils/avatarUtils'
import { createPreviewUrl, processAvatarImage, revokePreviewUrl, validateAvatarFile } from '../../utils/avatarUtils'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
`

const AvatarPreview = styled.div<{ $size?: number }>`
  position: relative;
  width: ${(props) => props.$size || 120}px;
  height: ${(props) => props.$size || 120}px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background};
  border: 3px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const InitialsPlaceholder = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textSecondary};
`

const UploadOverlay = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  transition: ${theme.transitions.default};
  cursor: pointer;
  gap: ${theme.spacing.xs};

  &:hover {
    opacity: 1;
  }

  svg {
    color: white;
  }

  span {
    color: white;
    font-size: ${theme.typography.xs};
    font-weight: 500;
  }
`

const HiddenInput = styled.input`
  display: none;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  justify-content: center;
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  border: none;
  font-size: ${theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  ${({ $variant = 'secondary', theme }) => {
    switch ($variant) {
      case 'primary':
        return `
          background: ${theme.colors.primary};
          color: ${theme.colors.background};
          &:hover:not(:disabled) {
            opacity: 0.9;
          }
        `
      case 'danger':
        return `
          background: ${theme.colors.error};
          color: white;
          &:hover:not(:disabled) {
            opacity: 0.9;
          }
        `
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
          border: 1px solid ${theme.colors.border};
          &:hover:not(:disabled) {
            background: ${theme.colors.surfaceHover};
            border-color: ${theme.colors.borderHover};
          }
        `
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.typography.sm};
  text-align: center;
  max-width: 300px;
`

const SuccessMessage = styled.div`
  color: ${theme.colors.success};
  font-size: ${theme.typography.sm};
  text-align: center;
  max-width: 300px;
`

const HelpText = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.xs};
  text-align: center;
  max-width: 300px;
`

interface AvatarUploadProps {
  currentAvatarUrl?: string | null
  userName?: string
  size?: number
  onUpload: (file: File) => Promise<AvatarUploadResult>
  onRemove?: () => Promise<void>
  disabled?: boolean
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatarUrl,
  userName,
  size = 120,
  onUpload,
  onRemove,
  disabled = false,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentAvatarUrl || null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Get initials for placeholder
  const initials = userName
    ? userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?'

  // Update preview when currentAvatarUrl changes
  useEffect(() => {
    if (currentAvatarUrl && !selectedFile) {
      setPreviewUrl(currentAvatarUrl)
    }
  }, [currentAvatarUrl, selectedFile])

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && !currentAvatarUrl) {
        revokePreviewUrl(previewUrl)
      }
    }
  }, [previewUrl, currentAvatarUrl])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Clear previous messages
    setError(null)
    setSuccess(null)

    // Validate file
    const validation = validateAvatarFile(file)
    if (!validation.valid) {
      setError(validation.error || 'Invalid file')
      return
    }

    try {
      // Process image for preview
      const processedFile = await processAvatarImage(file, size * 2) // 2x for retina
      setSelectedFile(processedFile)

      // Create preview URL
      const preview = createPreviewUrl(processedFile)
      if (previewUrl && previewUrl !== currentAvatarUrl) {
        revokePreviewUrl(previewUrl)
      }
      setPreviewUrl(preview)
    } catch (err) {
      setError('Failed to process image. Please try another file.')
      console.error('Image processing error:', err)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setUploading(true)
    setError(null)
    setSuccess(null)

    try {
      const result = await onUpload(selectedFile)

      if (result.success) {
        setSuccess('Avatar updated successfully!')
        setSelectedFile(null)
        // Update preview to the uploaded URL
        if (result.url) {
          if (previewUrl && previewUrl !== currentAvatarUrl) {
            revokePreviewUrl(previewUrl)
          }
          setPreviewUrl(result.url)
        }
      } else {
        setError(result.error || 'Failed to upload avatar')
      }
    } catch (err) {
      setError('Failed to upload avatar. Please try again.')
      console.error('Upload error:', err)
    } finally {
      setUploading(false)
    }
  }

  const handleCancel = () => {
    if (selectedFile && previewUrl) {
      revokePreviewUrl(previewUrl)
    }
    setSelectedFile(null)
    setPreviewUrl(currentAvatarUrl || null)
    setError(null)
    setSuccess(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemove = async () => {
    if (!onRemove) return

    setUploading(true)
    setError(null)
    setSuccess(null)

    try {
      await onRemove()
      if (previewUrl && previewUrl !== currentAvatarUrl) {
        revokePreviewUrl(previewUrl)
      }
      setPreviewUrl(null)
      setSelectedFile(null)
      setSuccess('Avatar removed successfully!')
    } catch (err) {
      setError('Failed to remove avatar. Please try again.')
      console.error('Remove error:', err)
    } finally {
      setUploading(false)
    }
  }

  const handleOverlayClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click()
    }
  }

  return (
    <Container>
      <AvatarPreview $size={size}>
        {previewUrl ? <img src={previewUrl} alt="Avatar preview" /> : <InitialsPlaceholder>{initials}</InitialsPlaceholder>}

        <UploadOverlay $show={!disabled && !uploading} onClick={handleOverlayClick}>
          <Camera size={24} />
          <span>{previewUrl ? 'Change' : 'Upload'}</span>
        </UploadOverlay>
      </AvatarPreview>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileSelect}
        disabled={disabled || uploading}
      />

      {selectedFile && !uploading && (
        <ButtonGroup>
          <Button $variant="primary" onClick={handleUpload} disabled={uploading}>
            <Upload size={16} />
            Upload
          </Button>
          <Button onClick={handleCancel} disabled={uploading}>
            <X size={16} />
            Cancel
          </Button>
        </ButtonGroup>
      )}

      {!selectedFile && currentAvatarUrl && onRemove && (
        <Button $variant="danger" onClick={handleRemove} disabled={disabled || uploading}>
          <X size={16} />
          Remove Avatar
        </Button>
      )}

      {uploading && (
        <Button disabled>
          <Loader2 size={16} className="animate-spin" />
          Uploading...
        </Button>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {!error && !success && !selectedFile && (
        <HelpText>Supported formats: JPEG, PNG, WebP. Max size: 5MB. Image will be automatically resized.</HelpText>
      )}
    </Container>
  )
}
